import React, { useState, useEffect, useCallback } from 'react';
import {
  Coins,
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowUpDown,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Gift,
  RefreshCw,
} from 'lucide-react';
import walletService from '../../services/walletService';

const PER_PAGE = 20;

/** Idle time before a keystroke in the search box turns into a request. */
const SEARCH_DEBOUNCE_MS = 400;

const DIRECTION_TABS = [
  { id: 'all', label: 'All' },
  { id: 'incoming', label: 'Incoming' },
  { id: 'outgoing', label: 'Outgoing' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'customer', label: 'Customer Name' },
  { value: 'phone', label: 'Customer Phone' },
  { value: 'admin', label: "Admin's Name" },
  { value: 'adminPhone', label: "Admin's Phone" },
  { value: 'amount', label: 'Amount' },
];

// Every way coins can move, and how each reads in the ledger.
const SOURCE_META = {
  bank_transfer: { label: 'Bank Transfer', className: 'bg-purple-50 text-purple-700' },
  order_payment: { label: 'Order Payment', className: 'bg-green-50 text-green-700' },
  admin_credit: { label: 'Admin Credit', className: 'bg-rose-50 text-rose-700' },
  topup: { label: 'Top-up', className: 'bg-blue-50 text-blue-700' },
  refund: { label: 'Refund', className: 'bg-amber-50 text-amber-700' },
  credit: { label: 'Credit', className: 'bg-gray-100 text-gray-600' },
};

const currency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const StatCard = ({ icon: Icon, color, label, value, hint, loading, valueClassName = 'text-gray-800' }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className={`${color} w-11 h-11 rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    {loading ? (
      <div className="h-7 w-24 bg-gray-100 rounded animate-pulse mb-1" />
    ) : (
      <h3 className={`text-2xl font-bold tabular-nums ${valueClassName}`}>{value}</h3>
    )}
    <p className="text-gray-500 text-sm">{label}</p>
    {hint && <p className="text-gray-400 text-xs mt-1">{hint}</p>}
  </div>
);

const BmfCoinsManagement = () => {
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [direction, setDirection] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    const res = await walletService.getCoinsOverview();
    if (res.success) setOverview(res.data);
    setOverviewLoading(false);
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = { page, limit: PER_PAGE, direction, sortBy, sortOrder };
    if (search) params.search = search;
    const res = await walletService.getCoinTransactions(params);
    if (res.success) {
      setRows(res.data.data || []);
      const p = res.data.pagination || {};
      setTotalPages(p.totalPages || 1);
      setTotal(p.total || 0);
    } else {
      setError(res.error);
      setRows([]);
    }
    setLoading(false);
  }, [page, direction, sortBy, sortOrder, search]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Any change to the filters invalidates the current page number.
  useEffect(() => { setPage(1); }, [direction, sortBy, sortOrder, search]);

  // Debounce the search box so typing a phone number isn't one request per digit.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortOrder(key === 'date' || key === 'amount' ? 'DESC' : 'ASC');
    }
  };

  // Tailwind classes are written out in full — a template like `text-${align}`
  // is invisible to the JIT scanner and would be purged from the build.
  const SortableHeader = ({ label, sortKey, align = 'left' }) => (
    <th className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-3 text-gray-500 font-medium`}>
      <button
        onClick={() => toggleSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-gray-800 transition-colors ${
          sortBy === sortKey ? 'text-[#548B2E] font-semibold' : ''
        }`}
      >
        {label}
        <ArrowUpDown className="w-3 h-3" />
        {sortBy === sortKey && <span className="text-[10px]">{sortOrder === 'ASC' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );

  const refreshAll = () => { fetchOverview(); fetchRows(); };

  // Net coins redeemed this month = spent − issued. Backend sends it directly;
  // fall back to deriving it so an older API response still renders a number.
  const netRedeemedThisMonth =
    overview?.netRedeemedThisMonth ??
    ((overview?.redeemedThisMonth || 0) - (overview?.issuedThisMonth || 0));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">BMF Coins</h1>
          <p className="text-gray-500 text-sm">Coins in circulation and every movement in and out of customer wallets</p>
        </div>
        <button
          onClick={refreshAll}
          className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Coins}
          color="bg-[#548B2E]"
          label="Total in Circulation"
          value={currency(overview?.totalInCirculation)}
          hint="Outstanding balance across all wallets"
          loading={overviewLoading}
        />
        <StatCard
          icon={TrendingUp}
          color="bg-rose-500"
          label="Total Issued (all time)"
          value={currency(overview?.totalIssued)}
          hint="Admin credits, refunds and top-ups"
          loading={overviewLoading}
        />
        <StatCard
          icon={TrendingDown}
          color="bg-emerald-600"
          label="Total Redeemed (all time)"
          value={currency(overview?.totalRedeemed)}
          hint="Coins spent on orders"
          loading={overviewLoading}
        />
        <StatCard
          icon={Wallet}
          color="bg-amber-500"
          label="Net Issued (all time)"
          value={currency((overview?.totalIssued || 0) - (overview?.totalRedeemed || 0))}
          hint="Issued minus redeemed"
          loading={overviewLoading}
        />

        <StatCard
          icon={Gift}
          color="bg-red-500"
          label="Issued This Month"
          value={currency(overview?.issuedThisMonth)}
          hint="Admin credits and refunds this month"
          loading={overviewLoading}
        />
        <StatCard
          icon={ArrowDownLeft}
          color="bg-green-600"
          label="Redeemed This Month"
          value={currency(overview?.redeemedThisMonth)}
          hint="Coins spent on orders this month"
          loading={overviewLoading}
        />
        <StatCard
          icon={ArrowUpDown}
          color="bg-slate-600"
          label="Net Redeemed (This Month)"
          value={currency(netRedeemedThisMonth)}
          valueClassName={netRedeemedThisMonth < 0 ? 'text-red-600' : 'text-green-600'}
          hint="Redeemed minus issued this month"
          loading={overviewLoading}
        />
        <StatCard
          icon={Users}
          color="bg-indigo-500"
          label="Coin Holders"
          value={overviewLoading ? '' : (overview?.coinHolders ?? 0).toLocaleString('en-IN')}
          hint={overview ? `Avg ${currency(overview.averageBalance)} each` : ''}
          loading={overviewLoading}
        />
      </div>

      {/* Direction tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {DIRECTION_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setDirection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              direction === tab.id
                ? 'bg-[#548B2E] text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.id === 'incoming' && <ArrowDownLeft className="w-4 h-4" />}
            {tab.id === 'outgoing' && <ArrowUpRight className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-gray-500 mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Customer or admin name, phone number…"
                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/30"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]/30"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
            title={sortOrder === 'ASC' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'ASC' ? 'Asc ↑' : 'Desc ↓'}
          </button>
          <span className="self-end pb-2 text-xs text-gray-400 ml-auto">
            {total.toLocaleString('en-IN')} transaction{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">ID</th>
                <SortableHeader label="Customer" sortKey="customer" />
                <SortableHeader label="Mobile" sortKey="phone" />
                <SortableHeader label="Date & Time" sortKey="date" />
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Source</th>
                <th className="text-left px-4 py-3 text-gray-500 font-medium">Transaction Ref ID</th>
                <SortableHeader label="Admin" sortKey="admin" />
                <SortableHeader label="Amount" sortKey="amount" align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {[40, 140, 110, 120, 90, 110, 120, 80].map((w, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: w }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-red-500">{error}</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    No BMF Coin transactions found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const meta = SOURCE_META[row.source] || SOURCE_META.credit;
                  const isIncoming = row.direction === 'incoming';
                  return (
                    <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 tabular-nums">#{row.id}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {row.customer?.name || <span className="text-gray-300">Unknown</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 tabular-nums">
                        {row.customer?.phone || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        <div>{formatDate(row.createdAt)}</div>
                        <div className="text-xs text-gray-400">{formatTime(row.createdAt)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${meta.className}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 tabular-nums">
                        {row.transactionRefId || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {row.admin ? (
                          <>
                            <div className="text-gray-700">{row.admin.name}</div>
                            <div className="text-xs text-gray-400 tabular-nums">{row.admin.phone || '—'}</div>
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex items-center gap-1 font-semibold tabular-nums ${
                            isIncoming ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {isIncoming
                            ? <ArrowDownLeft className="w-3.5 h-3.5" />
                            : <ArrowUpRight className="w-3.5 h-3.5" />}
                          {currency(row.amount)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Admin attribution started with this release — credits issued before it show “—” and cannot be traced back.
      </p>
    </div>
  );
};

export default BmfCoinsManagement;
